/**
 * LoginPage — three-step login flow mirroring Portal-Ui's pattern.
 *
 * 1. Email step:    username/email input + "Login with Password" + "Login with SSO"
 * 2. Password step: pre-filled username (read-only) + password + submit + back
 * 3. SSO flow:      MSAL loginRedirect -> callback -> acquireTokenSilent -> whoAmI -> loginSso -> navigate
 *
 * Dev/Sandbox login buttons are preserved at the bottom when running in
 * development mode or VITE_SANDBOX_MODE.
 */
import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { useAuth } from '@shared/context/useAuth';
import type { SsoClaims } from '@shared/context/AuthContext';
import { normalizeRoles } from '@shared/utils/authUtils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const TOAST_DURATION_MS = 6000;

type LoginStep = 'email' | 'password' | 'link-account';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const { loginLegacy, loginSso, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ssoProcessing, setSsoProcessing] = useState(false);

  // Determine where to redirect after login
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  // Check for link-account mode from search params
  useEffect(() => {
    if (searchParams.get('linkAccount') === '1') {
      setStep('link-account');
    }
  }, [searchParams]);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated && !ssoProcessing) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, ssoProcessing, navigate, from]);

  // ── Handle MSAL redirect callback on page load ──
  useEffect(() => {
    let cancelled = false;

    async function handleRedirectResult() {
      // Only process if we're not already in a loading state
      if (loading || ssoProcessing) return;

      try {
        const { getMsalInstance } = await import('@shared/services/msalInstance');
        const msalInstance = getMsalInstance();
        await msalInstance.initialize();

        const result = await msalInstance.handleRedirectPromise();
        if (!result || cancelled) return;

        setSsoProcessing(true);

        // We have a redirect result — acquire token silently and call whoAmI
        const { loginRequest } = await import('@shared/services/msalConfig');
        const { loadConfig, getConfig } = await import('@shared/services/config');
        await loadConfig(); // Ensure config is loaded before getConfig() (Fix 7)
        const config = getConfig();

        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest(config.scope, result.account?.username),
          account: result.account!,
        });

        const { whoAmI } = await import('@shared/services/authentication');
        const data = await whoAmI(tokenResponse.accessToken);
        const payload = data.payload as SsoClaims;

        if (cancelled) return;

        const isNotRegistered = payload?.is_reg === 'False' || payload?.is_reg === false;
        const isRegistered = payload?.is_reg === 'True' || payload?.is_reg === true;

        if (isNotRegistered) {
          // User not registered — redirect to link-account
          navigate('/login?linkAccount=1');
          setSsoProcessing(false);
          return;
        }

        if (isRegistered) {
          if (payload.usr_status !== 'Active') {
            showToast('Your account has been deactivated. Please contact support.');
            setSsoProcessing(false);
            return;
          }

          loginSso(payload);
          navigateByRole(normalizeRoles(payload.cus_roles));
        }

        setSsoProcessing(false);
      } catch {
        if (!cancelled) {
          showToast('Single sign-on failed. Please try again or sign in with your username and password.');
          setSsoProcessing(false);
        }
      }
    }

    handleRedirectResult();

    return () => {
      cancelled = true;
    };
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Helpers ──

  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const navigateByRole = useCallback((roles: string[]) => {
    // For ClinicMgmt the default is dashboard
    void roles;
    navigate(from, { replace: true });
  }, [navigate, from]);

  // ── Email step actions ──

  const handlePasswordClick = () => {
    if (!email.trim()) {
      setError('Please enter a username or email.');
      return;
    }
    setError('');
    setStep('password');
  };

  const handleSsoClick = async () => {
    if (!email || !EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address for SSO.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const { getMsalInstance } = await import('@shared/services/msalInstance');
      const { loginRequest } = await import('@shared/services/msalConfig');
      const { getConfig } = await import('@shared/services/config');
      const config = getConfig();

      // Check for stale interaction
      if (localStorage.getItem('msal.interaction.status') === 'interaction_in_progress') {
        setLoading(false);
        return;
      }

      const msalInstance = getMsalInstance();
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();

      if (accounts?.length > 0) {
        // Existing MSAL session — try silent token acquisition
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest(config.scope, accounts[0].username),
            account: accounts[0],
          });

          const { whoAmI } = await import('@shared/services/authentication');
          const data = await whoAmI(tokenResponse.accessToken);
          const payload = data.payload as SsoClaims;

          const isNotRegistered = payload?.is_reg === 'False' || payload?.is_reg === false;
          const isRegistered = payload?.is_reg === 'True' || payload?.is_reg === true;

          if (isNotRegistered) {
            navigate('/login?linkAccount=1');
            setLoading(false);
            return;
          }

          if (isRegistered) {
            if (payload.usr_status !== 'Active') {
              showToast('Your account has been deactivated. Please contact support.');
              setLoading(false);
              return;
            }

            loginSso(payload);
            navigateByRole(normalizeRoles(payload.cus_roles));
          }

          setLoading(false);
        } catch {
          showToast('Single sign-on failed. Please try again or sign in with your username and password.');
          setLoading(false);
        }
      } else {
        // No existing session — redirect to Entra ID login
        const idpHint = getIdpHint(email.trim(), config);
        msalInstance.loginRedirect(loginRequest(config.scope, email.trim(), idpHint));
        // Page will redirect — don't set loading to false
      }
    } catch (err) {
      console.error('SSO login error:', err);
      setError('Failed to initiate SSO login. Please try again.');
      setLoading(false);
    }
  };

  // ── Password step actions ──

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your password.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const { loginUser } = await import('@shared/services/authentication');
      const result = await loginUser(email, password);

      if (result.token) {
        loginLegacy({
          bearerToken: result.token,
          refreshTokenId: result.refreshTokenId,
          expiresAtUtc: result.expiresAtUtc,
          identity: {
            ...result.identity,
            roles: normalizeRoles(result.identity?.roles),
          },
        });
        navigateByRole(normalizeRoles(result.identity?.roles));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ── Link-account actions ──

  const handleLinkAccount = async (e: FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your password.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const { getMsalInstance } = await import('@shared/services/msalInstance');
      const { loginRequest } = await import('@shared/services/msalConfig');
      const { getConfig } = await import('@shared/services/config');
      const { linkAccount } = await import('@shared/services/authentication');
      const config = getConfig();

      const msalInstance = getMsalInstance();
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();

      if (!accounts || accounts.length === 0) {
        throw new Error('No SSO session found. Please try SSO login again.');
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest(config.scope, accounts[0].username),
        account: accounts[0],
      });

      await linkAccount(tokenResponse.accessToken, email, password);
      await msalInstance.clearCache();

      setStep('email');
      setPassword('');
      showToast('Account linked successfully! Please log in again.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link account.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setError('');
    setPassword('');
    setStep('email');
  };

  // ── SSO enabled check (from loaded config, with fallback) ──
  const [ssoEnabled, setSsoEnabled] = useState(false);
  useEffect(() => {
    async function checkSso() {
      try {
        const { loadConfig, getConfig } = await import('@shared/services/config');
        await loadConfig(); // Ensure config is loaded before reading (Fix 8)
        const config = getConfig();
        setSsoEnabled(config.ssoEnabled);
      } catch {
        // Config load failed — SSO disabled by default
      }
    }
    checkSso();
  }, []);

  // ── Show full-page spinner during SSO redirect processing ──
  if (ssoProcessing) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <div className="login-brand-icon">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#0066cc" fill="rgba(0, 102, 204, 0.06)" />
                <rect x="9" y="12" width="6" height="10" rx="1" stroke="#00b4b4" fill="rgba(0, 180, 180, 0.15)" />
              </svg>
            </div>
            <h1>Examinetics</h1>
            <p>Clinic Network</p>
          </div>
          <div className="login-sso-status">
            <div className="spinner" />
            <p>Completing sign-in...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {toast && (
        <div className="login-toast" role="alert">
          {toast}
        </div>
      )}

      <div className="login-card">
        <div className="login-header" style={{ position: 'relative' }}>
          {step !== 'email' && (
            <button
              type="button"
              className="login-back-btn"
              onClick={handleBackClick}
              disabled={loading}
              aria-label="Back"
            >
              &#8592;
            </button>
          )}
          <div className="login-brand-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <h1>Examinetics</h1>
          <p>Clinic Network</p>
        </div>

        {/* ─── Email Step ─── */}
        {step === 'email' && (
          <>
            <div className="login-form">
              <div className="form-group">
                <label htmlFor="login-email">
                  {ssoEnabled ? 'Username or Email' : 'Username'}
                </label>
                <input
                  id="login-email"
                  type="text"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordClick(); }}
                  placeholder={ssoEnabled ? 'you@examinetics.com' : 'Username'}
                  disabled={loading}
                  autoFocus
                />
              </div>

              {error && <div className="login-error">{error}</div>}

              {ssoEnabled ? (
                <div className="login-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="btn-primary login-btn"
                    onClick={handlePasswordClick}
                    disabled={loading}
                  >
                    Login with Password
                  </button>
                  <button
                    type="button"
                    className="login-btn login-btn-sso"
                    onClick={handleSsoClick}
                    disabled={loading}
                  >
                    {loading ? 'Redirecting...' : 'Login with SSO'}
                  </button>
                </div>
              ) : (
                <div className="login-actions">
                  <button
                    type="button"
                    className="btn-primary login-btn"
                    onClick={handlePasswordClick}
                    disabled={loading}
                  >
                    Login with Password
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Password Step ─── */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="login-username">Username</label>
              <input
                id="login-username"
                type="text"
                value={email}
                disabled
                style={{ backgroundColor: '#f8fafc', color: '#64748b' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter your password"
                disabled={loading}
                autoFocus
              />
            </div>

            {error && <div className="login-error">{error}</div>}

            <div className="login-actions">
              <button
                type="submit"
                className="btn-primary login-btn"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>
        )}

        {/* ─── Link Account Step ─── */}
        {step === 'link-account' && (
          <form onSubmit={handleLinkAccount} className="login-form">
            <div className="login-link-info">
              Your SSO account is not linked. Please enter your existing credentials to link it.
            </div>

            <div className="form-group">
              <label htmlFor="link-username">Username</label>
              <input
                id="link-username"
                type="text"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="Username"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="link-password">Password</label>
              <input
                id="link-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Password"
                disabled={loading}
                autoFocus
              />
            </div>

            {error && <div className="login-error">{error}</div>}

            <div className="login-actions">
              <button
                type="submit"
                className="btn-primary login-btn"
                disabled={loading}
              >
                {loading ? 'Linking...' : 'Link Account'}
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Look up an IDP hint for the user's email domain (for federated SSO).
 */
function getIdpHint(
  emailAddress: string,
  config: { externalIDPs: Record<string, string> },
): string | undefined {
  if (!emailAddress || !EMAIL_REGEX.test(emailAddress)) return undefined;
  const domain = emailAddress.split('@')[1].toLowerCase();
  return config.externalIDPs[domain] ?? undefined;
}
